/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useSystemConfig } from '@/hooks/use-system-config'
import { Skeleton } from '@/components/ui/skeleton'

type AuthLayoutProps = {
  children: React.ReactNode
}

export function AuthLayout({ children }: AuthLayoutProps) {
  const { t } = useTranslation()
  const { systemName, logo, loading } = useSystemConfig()

  return (
    <div className='relative grid h-svh max-w-none bg-[var(--background)]'>
      {/* 背景装饰：柔和渐变 */}
      <div className='pointer-events-none fixed inset-0 overflow-hidden'>
        <div className='absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-[var(--primary-from)]/6 to-transparent blur-3xl' />
        <div className='absolute -bottom-40 -left-40 h-[400px] w-[400px] rounded-full bg-gradient-to-tr from-[var(--info)]/4 to-transparent blur-3xl' />
      </div>

      <Link
        to='/'
        className='absolute top-4 left-4 z-10 flex items-center gap-2 transition-all duration-200 [transition-timing-function:var(--ease-out-expo)] hover:opacity-80 sm:top-8 sm:left-8'
      >
        <div className='relative h-8 w-8'>
          {loading ? (
            <Skeleton className='absolute inset-0 rounded-full' />
          ) : (
            <img
              src={logo}
              alt={t('Logo')}
              className='h-8 w-8 rounded-full object-cover'
            />
          )}
        </div>
        {loading ? (
          <Skeleton className='h-6 w-24' />
        ) : (
          <h1 className='text-xl font-medium'>{systemName}</h1>
        )}
      </Link>
      <div className='container flex items-center pt-16 sm:pt-0'>
        {/* macOS 风格毛玻璃卡片 */}
        <div
          className='mx-auto flex w-full flex-col justify-center space-y-2 rounded-[var(--radius-card)] px-[var(--spacing-auth-x)] py-8 shadow-[var(--shadow-card)] backdrop-blur-[20px] saturate-[180%] [animation:fade-in-up_0.6s_var(--ease-out-expo)_both] sm:w-[480px] sm:p-8'
          style={{ backgroundColor: 'var(--bg-glass)' }}
        >
          <div className='[animation:fade-in-up_0.6s_var(--ease-out-expo)_0.15s_both]'>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}